import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createThread, sendMessage, streamMessage } from './quizAgentAsync';
import { appendStreamChunk, completeStream } from './quizAgentActions';
import QuizAgentMessage from 'models/QuizAgentMessage';

interface IState {
  threadId: string | null;
  // requestId of the most recent createThread call. A stale createThread can
  // still resolve after being abandoned (e.g. the abort lost the race with
  // an already-in-flight response) — this lets fulfilled/rejected ignore
  // anything but the latest call instead of clobbering state with an old
  // thread/draftQuiz belonging to a session that's no longer active.
  currentThreadRequestId: string | null;
  messages: QuizAgentMessage[];
  draftQuiz: any;
  loading: boolean;
  streamingActivities: string[];
}

const initialState: IState = {
  threadId: null,
  currentThreadRequestId: null,
  messages: [],
  draftQuiz: {},
  loading: false,
  streamingActivities: [],
};

const quizAgentSlice = createSlice({
  name: 'quizAgent',
  initialState,
  reducers: {
    addUserMessage(state, action: PayloadAction<string>) {
      state.messages.push({ role: 'user', content: action.payload });
    },
    setInitialState: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      // createThread
      .addCase(createThread.pending, (state, action) => {
        state.loading = true;
        state.currentThreadRequestId = action.meta.requestId;
      })
      .addCase(createThread.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentThreadRequestId) return;
        state.threadId = action.payload.threadId;
        state.draftQuiz = action.payload.draftQuiz;
      })

      // sendMessage
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.messages.push(action.payload.message);
        state.draftQuiz = action.payload.draftQuiz;
      })

      // streamMessage
      .addCase(streamMessage.pending, (state) => {
        state.loading = true;
        state.streamingActivities = [];
      })
      .addCase(streamMessage.fulfilled, (state) => {
        state.loading = false;
        state.streamingActivities = [];
      })
      .addCase(streamMessage.rejected, (state) => {
        state.streamingActivities = [];
      })
      .addCase(appendStreamChunk, (state, action) => {
        if (action.payload.activities) {
          state.streamingActivities = action.payload.activities;
        }
        if (action.payload.draftQuiz) {
          state.draftQuiz = action.payload.draftQuiz;
        }
      })
      .addCase(completeStream, (state, action) => {
        if (action.payload.message) {
          state.messages.push(action.payload.message);
        }
        if (action.payload.draftQuiz) {
          state.draftQuiz = action.payload.draftQuiz;
        }
        state.streamingActivities = [];
      })

      .addMatcher((action) => action.type.endsWith('/rejected'), (state) => {
        state.loading = false;
      });
  },
});

export const QuizAgentActions = quizAgentSlice.actions;

export default quizAgentSlice.reducer;
